/**
 * DashboardsListPage (P3-S2 Doc 42).
 */
import { Eye, LayoutDashboard, Sparkles, Trash2 } from "lucide-react";
import * as React from "react";

import { type DashboardSummary, dashboardsApi } from "@/api/dashboards";
import { GenerateDashboardDialog } from "@/components/dashboard/GenerateDashboardDialog";
import { EmptyData } from "@/components/illustrations/EmptyData";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { LoadingCards } from "@/components/ui/loading-cards";
import { useUIStore } from "@/stores/ui";

export function DashboardsListPage(): JSX.Element {
  const pushToast = useUIStore((s) => s.pushToast);
  const [items, setItems] = React.useState<DashboardSummary[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [genOpen, setGenOpen] = React.useState(false);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await dashboardsApi.list();
      setItems(r.items);
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Không tải được list",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setLoading(false);
    }
  }, [pushToast]);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  const open = (code: string): void => {
    window.location.hash = `/admin/dashboards/${code}`;
  };

  const handleArchive = async (d: DashboardSummary): Promise<void> => {
    if (!confirm(`Archive dashboard "${d.title}"?`)) return;
    try {
      await dashboardsApi.archive(d.code);
      pushToast({ variant: "success", title: "Đã archive" });
      await refresh();
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Archive thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-4">
      <header className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <LayoutDashboard className="size-5 text-primary" />
            Dashboards
          </h1>
          <p className="text-sm text-muted-foreground">
            Generative dashboards. Mô tả intent → AI gen widgets.
          </p>
        </div>
        <Button variant="brand" size="sm" onClick={() => setGenOpen(true)}>
          <Sparkles className="size-4 mr-1" /> Generate dashboard
        </Button>
      </header>

      {loading ? (
        <LoadingCards count={4} />
      ) : items.length === 0 ? (
        <EmptyState
          hero={<EmptyData size={120} />}
          title="Chưa có dashboard nào"
          description="Mô tả dashboard bạn muốn (vd doanh số 7 ngày, top khách hàng, audit activity) → AI tự gen widgets."
          action={
            <Button variant="brand" onClick={() => setGenOpen(true)}>
              <Sparkles className="size-4 mr-1" /> Generate dashboard đầu tiên
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((d) => (
            <Card
              key={d.id}
              className="hover:border-primary/50 cursor-pointer transition-colors"
              onClick={() => open(d.code)}
            >
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start gap-2">
                  <LayoutDashboard className="size-4 text-primary shrink-0 mt-1" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">
                      {d.title}
                    </div>
                    <code className="text-[10px] text-muted-foreground">
                      {d.code}
                    </code>
                  </div>
                  <Badge variant="outline" className="text-[10px] shrink-0">
                    {d.widget_count} widgets
                  </Badge>
                </div>
                {d.description ? (
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {d.description}
                  </p>
                ) : null}
                <div className="flex items-center justify-between gap-1 pt-1">
                  <span className="text-[10px] text-muted-foreground">
                    Updated {new Date(d.updated_at).toLocaleDateString("vi-VN")}
                  </span>
                  <div
                    className="flex gap-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button
                      size="icon"
                      variant="ghost"
                      className="size-7"
                      onClick={() => open(d.code)}
                      title="Open"
                    >
                      <Eye className="size-3.5" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="size-7 text-muted-foreground hover:text-destructive"
                      onClick={() => void handleArchive(d)}
                      title="Archive"
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <GenerateDashboardDialog
        open={genOpen}
        onClose={() => setGenOpen(false)}
        onCreated={refresh}
      />
    </div>
  );
}
